import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiPost } from '../utils/backendApi';
import { IS_UAT } from '../data/env';

// The ship rail shared by the super admin's bug (/admin/bugs/:id) and
// feature (/admin/features/:id) pages: the agent event stream, the
// attachment gallery, and the two release steps (Merge & build → UAT,
// Approve & promote) that call the same server routes under a different
// base path (`/bugs/:id` or `/features/:id`). Both server sides reuse
// api/releaseApi.js startBuild / startPromote, so the gates shown here are
// the Releases tab's gates.

export function when(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}
export const short = (sha) => (sha ? String(sha).slice(0, 10) : '');
export const fmtBytes = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
export const stepClass = (state) => `bug-step${state === 'done' ? ' is-done' : state === 'here' ? ' is-here' : ''}`;
export const PLANE_BLOCK = IS_UAT ? 'ship steps run from the prod control plane (nventr.io)' : '';

// One busy/error/notice triple for a page of actions.
export function useActions(reload) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const act = async (label, fn) => {
    setBusy(label);
    setNotice('');
    setError('');
    try {
      const out = await fn();
      if (out?.message) setNotice(out.message);
      if (reload) await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };
  return { busy, error, notice, act, setError, setNotice };
}

export function Gate({ label, value }) {
  // value: true ok · false failed · null/undefined not run
  const tone = value === true ? 'ok' : value === false ? 'danger' : '';
  return <span className={`mf-chip${tone ? ` is-${tone}` : ''}`}>{label} {value === true ? '✓' : value === false ? '✗' : '—'}</span>;
}

export function Event({ ev }) {
  const ok = ev.status === 'finished' || ev.status === 'info' || ev.status === 'ok';
  const err = ev.status === 'error' || ev.error;
  const tag = ev.type === 'tool' ? `${ev.name}${ev.status ? ` · ${ev.status}` : ''}` : ev.type === 'status' ? `status${ev.status ? ` · ${ev.status}` : ''}` : ev.type === 'result' ? `result · ${ev.status || ''}` : ev.type;
  const text = ev.type === 'tool'
    ? [ev.args, ev.result].filter(Boolean).join('\n')
    : ev.type === 'status' ? ev.message : ev.text;
  return (
    <div className={`bug-ev is-${ev.type}${ok ? ' is-ok' : ''}${err ? ' is-error' : ''}`}>
      <span className="bug-ev-tag">{tag}{ev.by ? ` · ${ev.by}` : ''}</span>
      {text ? <p className="bug-ev-text">{String(text).slice(0, 6000)}</p> : null}
      {ev.error ? <p className="bug-ev-text">{ev.error}</p> : null}
      {ev.prUrl ? <a href={ev.prUrl} target="_blank" rel="noreferrer">{ev.prUrl}</a> : null}
    </div>
  );
}

// Live agent stream + follow-up box. `onSend(text)` / `onCancel()` do the
// API call; the panel owns only the input.
export function AgentPanel({ events, fix, agentBusy, busy, emptyText, placeholder, onSend, onCancel }) {
  const [followup, setFollowup] = useState('');
  const send = async () => { const t = followup.trim(); if (!t) return; await onSend(t); setFollowup(''); };
  return (
    <div className="cs-sw-card" style={{ marginTop: 16 }}>
      <h3>Agent</h3>
      <div className="bug-events">
        {!events?.length && <span className="bug-ev-empty">{emptyText}</span>}
        {(events || []).map((ev) => <Event key={ev.id} ev={ev} />)}
        {agentBusy && <span className="bug-ev-empty">working…</span>}
      </div>
      {fix?.agentId && (
        <div className="bug-followup">
          <input className="cs-input" value={followup} placeholder={placeholder} onChange={(e) => setFollowup(e.target.value)} disabled={agentBusy || !!busy} onKeyDown={(e) => { if (e.key === 'Enter') send(); }} />
          <button type="button" className="cs-btn cs-btn-sm" onClick={send} disabled={agentBusy || !!busy || !followup.trim()}>Send</button>
          {agentBusy && <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={onCancel} disabled={!!busy}>Cancel run</button>}
        </div>
      )}
      {fix?.lastError && <p className="cs-land-micro" role="alert">{fix.lastError}</p>}
    </div>
  );
}

export function AttachmentGallery({ attachments }) {
  if (!attachments?.length) return null;
  return (
    <div className="bug-gallery">
      {attachments.map((a, i) => (
        <figure key={`${a.key}-${i}`} style={{ margin: 0 }}>
          {a.url && /^image\//.test(a.type || '') ? (
            <a href={a.url} target="_blank" rel="noreferrer"><img src={a.url} alt={a.name} /><figcaption>{a.name}</figcaption></a>
          ) : (
            <a href={a.url || '#'} target="_blank" rel="noreferrer" className="bug-gallery-file">▤ {a.name} <span className="cs-help">{fmtBytes(a.size)}</span></a>
          )}
        </figure>
      ))}
    </div>
  );
}

export function PrFacts({ pr }) {
  if (!pr || pr.error) return null;
  return (
    <p className="cs-help">
      {pr.title} · {pr.state}{pr.merged ? ' · merged' : ''} · +{pr.additions ?? '?'} −{pr.deletions ?? '?'} in {pr.changedFiles ?? '?'} file{pr.changedFiles === 1 ? '' : 's'}
      {pr.mergeable === false ? ' · conflicts — ask the agent to rebase' : ''}
    </p>
  );
}

// What the two release steps need to know about the linked release.
export function releaseFacts(release, status) {
  const uatDone = release?.uat?.status === 'done';
  const evalsFail = !!(release?.evals && release.evals.ok === false);
  const featuresFail = !!(release?.platformHealth && release.platformHealth.ok === false);
  return {
    uatDone,
    evalsFail,
    featuresFail,
    needsForce: evalsFail || featuresFail,
    onProd: release?.status === 'prod' || status === 'shipped',
  };
}

// Step: squash-merge the PR and Build → UAT. `base` is `/bugs/:id` or
// `/features/:id`; `act` is the page's action runner.
export function MergeBuildStep({ n, state, base, fix, release, status, agentBusy, busy, closed, act }) {
  const { uatDone } = releaseFacts(release, status);
  const kit = fix?.repo === 'kit';
  const run = () => act('merge', async () => {
    const out = await apiPost(`${base}/merge-build`, {});
    return { message: out.repo === 'kit' ? `Merged on the kit as ${short(out.mergedSha)} — it rides the next kit release; the report closes when the reporter's instance runs it.` : `Merged as ${short(out.mergedSha)} — Build → UAT ${out.reused ? 'joined the running build' : 'queued'}.` };
  });
  return (
    <div className={stepClass(state)}>
      <div className="bug-step-head">
        <h4>{n} · {kit ? 'Merge into kit main' : 'Merge & build to UAT'}</h4>
        {kit && fix?.mergedSha ? <span className={`mf-chip ${fix?.kitRef ? 'is-ok' : 'is-info'}`}>{fix?.kitRef ? `in ${fix.kitRef}` : 'awaiting kit release'}</span> : null}
        {!kit && fix?.releaseSha ? <span className={`mf-chip ${uatDone ? 'is-ok' : release?.uat?.status === 'error' ? 'is-danger' : 'is-info'}`}>UAT {release?.uat?.status || 'queued'}</span> : null}
      </div>
      <p className="cs-help">{kit
        ? 'Squash-merges the PR into kit main with the Bug/Feature trailer. The plane tags green main and rolls it; the report closes when the reporter\'s instance runs that release.'
        : 'Squash-merges the PR into main and runs Build → UAT: image once, UAT roll, hosting + rules, golden evals, feature journeys.'}</p>
      <div className="bug-step-actions" data-agent-guard>
        <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={run} disabled={!!busy || !fix?.prNumber || agentBusy || !!fix?.mergedSha || !!PLANE_BLOCK || closed} title={PLANE_BLOCK}>
          {busy === 'merge' ? 'Merging…' : fix?.mergedSha ? `Merged ${short(fix.mergedSha)}` : kit ? 'Merge into kit main' : 'Merge & build to UAT'}
        </button>
        {release && <Link to="/admin/apps" className="cs-help">release {short(release.id)} →</Link>}
      </div>
      {release && (
        <p className="cs-help" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Gate label="UAT" value={uatDone ? true : release.uat?.status === 'error' ? false : null} />
          <Gate label="evals" value={release.evals ? release.evals.ok !== false : null} />
          <Gate label="features" value={release.platformHealth ? release.platformHealth.ok !== false : null} />
          {release.uat?.error ? <span className="cs-land-micro">{release.uat.error}</span> : null}
        </p>
      )}
    </div>
  );
}

// Step: confirm on UAT, promote the same image to prod. Same gates as the
// Releases tab; `confirm` word + optional force.
export function PromoteStep({ n, state, base, fix, release, status, busy, act, successMessage }) {
  const [confirmWord, setConfirmWord] = useState('');
  const [force, setForce] = useState(false);
  const { uatDone, evalsFail, featuresFail, needsForce, onProd } = releaseFacts(release, status);
  const run = () => act('approve', async () => {
    await apiPost(`${base}/approve`, { confirm: 'promote', force });
    setConfirmWord('');
    setForce(false);
    return { message: successMessage };
  });
  if (fix?.repo === 'kit') {
    return (
      <div className={stepClass(state)}>
        <div className="bug-step-head">
          <h4>{n} · Kit release</h4>
          {status === 'shipped' ? <span className="mf-chip is-ok">live on the reporter's app</span> : fix?.kitRef ? <span className="mf-chip is-info">in {fix.kitRef}</span> : null}
        </div>
        <p className="cs-help">No promote here: the kit's auto release tags main and rolls the fleet; instances take it in their window or on Update now. The reporter is emailed when their instance runs it.</p>
      </div>
    );
  }
  return (
    <div className={stepClass(state)}>
      <div className="bug-step-head">
        <h4>{n} · Approve &amp; promote</h4>
        {onProd ? <span className="mf-chip is-ok">live on prod</span> : release?.status === 'promoting' ? <span className="mf-chip is-info">promoting</span> : null}
      </div>
      <p className="cs-help">Confirm it on UAT ({uatDone ? 'built' : 'not built yet'}), then promote the same image to production. Everyone waiting on it gets an email when it lands.</p>
      {!onProd && fix?.releaseSha && (
        <>
          <input className="cs-input" value={confirmWord} onChange={(e) => setConfirmWord(e.target.value)} placeholder="type promote" disabled={!!busy || !uatDone} aria-label="confirm word" data-agent-noclick />
          {needsForce && (
            <label className="cs-help" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
              force despite {[evalsFail && `golden evals failed (${release.evals.reason})`, featuresFail && `${release.platformHealth.failing?.length || ''} feature journeys failed`].filter(Boolean).join(' and ')}
            </label>
          )}
        </>
      )}
      <div className="bug-step-actions" data-agent-guard>
        <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={run} disabled={!!busy || onProd || !fix?.releaseSha || !uatDone || confirmWord !== 'promote' || (needsForce && !force) || !!PLANE_BLOCK || release?.status === 'promoting'} title={PLANE_BLOCK}>
          {busy === 'approve' ? 'Promoting…' : onProd ? 'Promoted' : 'Approve & promote'}
        </button>
      </div>
      {release?.prod?.error ? <p className="cs-land-micro">{release.prod.error}</p> : null}
    </div>
  );
}
